import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RouterAdapterProvider, type RouterAdapter, type UrlState } from '@resource-kit/core/react';

/**
 * The host's implementation of the two core router seams (#1801).
 *
 * This is the ONLY place the resource kit's URL state meets react-router. Core
 * declares the seams; this file satisfies them; `App.tsx` installs it once.
 */

function useReactRouterUrlState(): UrlState {
  const [params, setSearchParams] = useSearchParams();
  const setParams = useCallback<UrlState['setParams']>(
    (update, options) => {
      setSearchParams((previous) => update(previous), { replace: options?.replace ?? false });
    },
    [setSearchParams],
  );
  return useMemo(() => ({ params, setParams }), [params, setParams]);
}

/**
 * Guards the browser-level exits (reload, tab close, back out of the SPA).
 *
 * It does NOT block in-app navigation: react-router's `useBlocker` requires a
 * data router, and this app mounts a plain `<BrowserRouter>`. That is a real
 * limitation of THIS adapter, not of the seam — swapping in a data router
 * upgrades every consumer at once, which is the point of having the seam.
 */
function useBeforeUnloadBlocker(blocked: boolean, message?: string): void {
  useEffect(() => {
    if (!blocked) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Browsers ignore the custom text and show their own copy; assigning
      // `returnValue` is still what triggers the prompt at all.
      event.returnValue = message ?? '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [blocked, message]);
}

/** Module-level constant so the provider value never changes identity. */
export const reactRouterAdapter: RouterAdapter = {
  useUrlState: useReactRouterUrlState,
  useNavigationBlocker: useBeforeUnloadBlocker,
};

export function ReactRouterAdapterProvider({ children }: { children: ReactNode }) {
  return <RouterAdapterProvider adapter={reactRouterAdapter}>{children}</RouterAdapterProvider>;
}
